import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#4f46e5',
      light: '#818cf8',
      dark: '#3730a3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#06b6d4',
      light: '#67e8f9',
      dark: '#0891b2',
    },
    background: {
      default: '#fafbff',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
    success: { main: '#10b981', light: '#d1fae5', dark: '#047857' },
    warning: { main: '#f59e0b', light: '#fef3c7', dark: '#d97706' },
    error: { main: '#ef4444', light: '#fee2e2', dark: '#dc2626' },
    info: { main: '#3b82f6', light: '#dbeafe', dark: '#1d4ed8' },
    divider: 'rgba(148, 163, 184, 0.12)',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 },
    h2: { fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.1 },
    h3: { fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h4: { fontWeight: 700, letterSpacing: '-0.015em', lineHeight: 1.25 },
    h5: { fontWeight: 700, lineHeight: 1.35 },
    h6: { fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontWeight: 500, lineHeight: 1.5, fontSize: '1.05rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.875rem', letterSpacing: '0.02em' },
    body1: { lineHeight: 1.75, color: '#475569' },
    body2: { lineHeight: 1.65 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
    caption: { fontWeight: 500, letterSpacing: '0.02em' },
  },
  shape: { borderRadius: 16 },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.03)',
    '0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.06)',
    '0 4px 6px -1px rgb(0 0 0 / 0.07), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
    '0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.03)',
    '0 20px 25px -5px rgb(0 0 0 / 0.08), 0 8px 10px -6px rgb(0 0 0 / 0.03)',
    '0 25px 50px -12px rgb(0 0 0 / 0.15)',
    ...Array(18).fill('0 25px 50px -12px rgb(0 0 0 / 0.15)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { scrollBehavior: 'smooth' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '11px 24px',
          fontSize: '0.875rem',
          fontWeight: 600,
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)',
          backgroundSize: '200% 200%',
          boxShadow: '0 4px 15px rgba(79, 70, 229, 0.4), 0 1px 3px rgba(79, 70, 229, 0.2)',
          '&:hover': {
            backgroundPosition: '100% 0',
            boxShadow: '0 8px 25px rgba(79, 70, 229, 0.5), 0 2px 6px rgba(79, 70, 229, 0.3)',
            transform: 'translateY(-2px)',
          },
          '&:active': { transform: 'translateY(0)' },
        },
        containedSecondary: {
          background: 'linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%)',
          boxShadow: '0 4px 15px rgba(6, 182, 212, 0.35)',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(6, 182, 212, 0.5)',
            transform: 'translateY(-2px)',
          },
        },
        containedSuccess: {
          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.35)',
          '&:hover': { transform: 'translateY(-2px)' },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' },
        },
        sizeLarge: { padding: '14px 36px', fontSize: '0.95rem', borderRadius: 14 },
        sizeSmall: { padding: '7px 16px', fontSize: '0.8rem', borderRadius: 10 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          border: '1px solid rgba(148, 163, 184, 0.08)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 20px 40px rgba(0,0,0,0.08), 0 8px 16px rgba(79,70,229,0.04)',
            transform: 'translateY(-6px)',
            borderColor: 'rgba(79, 70, 229, 0.12)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation0: { border: '1px solid rgba(148, 163, 184, 0.1)' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease',
            backgroundColor: '#fafbff',
            '&:hover': { backgroundColor: '#fff' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#a5b4fc' },
            '&.Mui-focused': { backgroundColor: '#fff' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '2px', borderColor: '#4f46e5' },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 10, fontSize: '0.75rem' },
        outlined: { borderWidth: '1.5px' },
        colorPrimary: { background: 'rgba(79, 70, 229, 0.08)', color: '#4f46e5', border: 'none' },
      },
    },
    MuiAvatar: {
      styleOverrides: { root: { fontWeight: 700 } },
    },
    MuiTab: {
      styleOverrides: { root: { fontWeight: 600, textTransform: 'none', fontSize: '0.9rem', borderRadius: 10 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 14 } },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 20 } },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { borderRadius: 10, fontSize: '0.75rem', fontWeight: 500, padding: '8px 14px' } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 10, height: 6 } },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { fontWeight: 700, fontSize: '0.65rem' },
      },
    },
  },
});

export default theme;